import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSupabaseAuth } from '@hooks/useSupabaseAuth';
import { RoleType } from 'types/roles';

import { showToast } from '@components/ToastContainer';

import { 
  createSignedImageUrl,
  getUserImageData, 
  uploadUserImage, 
  validateImageFile
} from '@utils/helpers/imageUploadHelpers';

interface UseImageUploadOptions {
  onImageUpload?: (imagePath: string) => void;
  onError?: (error: string) => void;
  successMessage?: string;
  errorTitle?: string;
  successTitle?: string;
}

export const useImageUpload = (options: UseImageUploadOptions = {}) => {
  const { 
    onImageUpload, 
    onError, 
    errorTitle,
  } = options;
  
  const { t } = useTranslation('common');
  const { user, isLoading: isUserLoading } = useSupabaseAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(false);
  const [userRole, setUserRole] = useState<RoleType | null>(null);

  // Load user data and current image
  useEffect(() => {
    if (isUserLoading) return;

    const loadUserData = async () => {
      try {
        if (!user) {
          console.error('User not available for photo upload.');
          return;
        }
        const userData = await getUserImageData(user);
        setUserRole(userData.role);
        setCurrentPath(userData.currentPath);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load user data';
        onError?.(errorMessage);
        // Don't show toast for automatic load errors - only notify via onError callback
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user, isUserLoading, onError]);

  // Generate signed URL for current image
  useEffect(() => {
    if (!currentPath || previewUrl) return;

    const generateSignedUrl = async () => {
      try {
        const url = await createSignedImageUrl(currentPath);
        if (url) {
          setSignedUrl(url);
          setError(false);
        } else {
          setError(true);
          onError?.('Failed to load image');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load image';
        setError(true);
        onError?.(errorMessage);
      }
    };

    generateSignedUrl();

    // Auto-refresh every 50 minutes
    const interval = setInterval(generateSignedUrl, 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentPath, previewUrl, onError]);

  // Handle file upload
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userRole || !user) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      const errorMessage = validation.error || 'Please select a valid image file';
      onError?.(errorMessage);
      showToast('error', t('toast.errorPhotoUpload'), t('toast.errorPhotoUploadDescription'));
      return;
    }

    try {
      setUploading(true);
      
      // Show instant preview
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);

      // Upload file
      const result = await uploadUserImage(file, user, userRole, currentPath);
      
      if (!result.success) {
        throw new Error(result.error);
      }

      // Update state on success
      URL.revokeObjectURL(fileUrl);
      setPreviewUrl(null);
      setCurrentPath(result.newPath || null);
      
      // Notify callbacks
      if (result.newPath) {
        onImageUpload?.(result.newPath);
      }
      
    } catch (error) {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      onError?.(errorMessage);
      showToast('error', errorTitle || t('toast.errorPhotoUpload'), t('toast.errorPhotoUploadDescription'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const hasImage = Boolean(!loading && !error && (signedUrl || previewUrl));
  const imageUrl = previewUrl || signedUrl || '';

  return {
    // State
    currentPath,
    imageUrl,
    loading: loading || isUserLoading,
    uploading,
    error,
    hasImage,
    userRole,
    
    // Refs
    fileInputRef,
    
    // Actions
    handleFileChange,
    triggerFileSelect,
    
    // Utilities
    resetError: () => setError(false),
  };
}; 